/* vm-turnstile.js — explicit Cloudflare Turnstile rendering for ValveMan lead forms.
   Implicit rendering (bare .cf-turnstile divs scanned by api.js) proved unreliable:
   it silently skipped the request-quote form's widget and can't handle the RFQ
   modals' display:none containers. Explicit rendering is deterministic.

   RENDER CONTRACT: sections render <div class="cf-turnstile" data-sitekey="..."> and
   load api.js with ?render=explicit (no onload param — this file polls for the API so
   script order never matters). window.vmTurnstileRender() renders every visible,
   not-yet-rendered widget — the RFQ modal open handlers call it again on reveal. Each
   rendered container is stamped data-vm-ts-rendered="1" and data-vm-ts-id="<widget id>".

   ONE TOKEN, ONE SUBMIT (Oct 30 2026). A Turnstile token can be verified exactly once.
   What the quote API (valveman-storefront-api /api/quote, lib/turnstile.js +
   lib/handleQuote.js) does with each kind of token:
     - valid, unsent token      clean lead.
     - REUSED token             Cloudflare answers `timeout-or-duplicate`.
     - NO token                 the server sends response="" and Cloudflare answers
                                `missing-input-response`.
   The last two are filed IDENTICALLY: the quote is accepted (never rejected), but stamped
   "⚠️ FAILED BOT CHECK" in Pipedrive, alert recipients are copied and Klaviyo is skipped.
   So a missing token is NOT safe — it is only no worse than a spent one. The goal is
   always to send a valid, unsent token. Real customers were flagged whenever one page
   load sent a second quote (modal reopened after a success, "Send another message",
   resubmit after an error). Every form that posts a token follows this contract,
   through window.vmTurnstile:
     - token(el)      the widget's current token, or '' when it has none or it is SPENT
                      (already sent, or sitting in the widget when it was reset).
     - spend(el, t)   mark t as sent. Call it when the request goes out.
     - isSpent(el)    true when the token the widget still holds is a spent one.
     - reset(el)      fresh challenge for THIS widget only (never a bare reset(), which
                      hits whichever widget rendered last — e.g. the RFQ modal's).
                      Forms call it after EVERY response (success, server error,
                      network error), on modal reopen and on "Send another message" —
                      but ONLY when isSpent(el). After a response the widget still holds
                      the token just sent, so it is reset; if a reset already ran (e.g.
                      the modal was reopened mid-request) the widget holds a fresh, valid
                      token, and resetting again would throw it away — a visitor shown
                      the checkbox would have to tick it again or send none (flagged).
     - whenToken(el, ms, cb)  cb(token) as soon as an unspent token exists, or with ''
                      once ms of WALL-CLOCK time has passed (a deadline, so background-
                      tab timer throttling cannot stretch it). The forms then send
                      anyway: the lead is still accepted (flagged), whereas refusing to
                      send would lose it outright.
   Every function is a safe no-op when window.turnstile is undefined (blocked/offline)
   or the element is missing or not yet rendered. Vanilla JS only. */
(function () {
  'use strict';

  function api() { return window.turnstile || null; }

  function renderOne(el) {
    if (!window.turnstile || el.getAttribute('data-vm-ts-rendered')) return;
    // Skip widgets inside hidden containers (e.g. a closed modal) — rendering
    // there is unreliable; the modal-open handler re-invokes us.
    if (el.offsetParent === null && el.offsetWidth === 0 && el.offsetHeight === 0) return;
    try {
      var id = window.turnstile.render(el, {
        sitekey: el.getAttribute('data-sitekey'),
        theme: el.getAttribute('data-theme') || 'light'
      });
      if (id) el.setAttribute('data-vm-ts-id', String(id));
      el.setAttribute('data-vm-ts-rendered', '1');
    } catch (e) { /* leave unmarked so a later call can retry */ }
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.cf-turnstile'), renderOne);
  }

  /* Rendered by us, or by api.js implicitly (it injects an iframe / response input). */
  function rendered(el) {
    return !!(el && (el.getAttribute('data-vm-ts-rendered') ||
      el.querySelector('iframe, input[name="cf-turnstile-response"]')));
  }

  /* The widget id when we rendered it; otherwise the container element itself, which
     the Turnstile API also accepts as a widget reference. */
  function ref(el) { return el.getAttribute('data-vm-ts-id') || el; }

  function rawToken(el) {
    if (!api() || !rendered(el)) return '';
    try { return api().getResponse(ref(el)) || ''; } catch (e) { return ''; }
  }

  function token(el) {
    var t = rawToken(el);
    return t && t !== el.__vmTsSpent ? t : '';
  }

  function spend(el, t) { if (el && t) el.__vmTsSpent = t; }

  function isSpent(el) {
    var t = el ? rawToken(el) : '';
    return !!t && t === el.__vmTsSpent;
  }

  function reset(el) {
    if (!api() || !rendered(el)) return false;
    spend(el, rawToken(el)); // whatever the widget held is now unusable
    try { api().reset(ref(el)); return true; } catch (e) { return false; }
  }

  function whenToken(el, ms, cb) {
    var deadline = Date.now() + ms; // wall clock: throttled timers cannot stretch it
    (function poll() {
      var t = el ? token(el) : '';
      var left = deadline - Date.now();
      // Nothing to wait for when the API is absent or the widget never rendered.
      if (t || !api() || !rendered(el) || left <= 0) { cb(t); return; }
      setTimeout(poll, Math.min(200, left));
    })();
  }

  window.vmTurnstileRender = boot;
  window.vmTurnstile = { render: boot, token: token, spend: spend, isSpent: isSpent, reset: reset, whenToken: whenToken };

  // Wait for api.js (async — order is not guaranteed), then render.
  var tries = 0;
  (function poll() {
    if (window.turnstile) { boot(); return; }
    if (tries++ < 60) setTimeout(poll, 250); // give up after 15s (offline / blocked)
  })();
})();
